'use client';

import {
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';

import { cn } from '@ps/ui/utils';
import { type Descendant, Editor, type Node, Range, Transforms } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';

import {
  type ChannelMemberItem,
  fetchChannelMembers,
} from '../../functions/members/fetchChannelMembers';
import { ChannelAvatar } from '../common/ChannelAvatar';
import type { CustomEditor, MentionElement } from './types/SlateNodes';

export type MentionPickerRef = {
  /**
   * Hook the picker into the editor's `onChange`. Returns `true`
   * when the picker has consumed the event (so the host editor can
   * skip its own handling).
   */
  handleChange: (editor: CustomEditor, value: Descendant[]) => boolean;
  /**
   * Hook the picker into the editor's `onKeyDown`. Returns `true`
   * when the key was consumed (ArrowUp/Down/Enter/Tab/Escape while
   * the picker is open).
   */
  handleKeyDown: (
    editor: CustomEditor,
    event: KeyboardEvent<HTMLDivElement>,
  ) => boolean;
};

interface Props {
  /**
   * Channel context. The picker only opens while the composer is
   * inside a channel — the host wires in the (creatorId, channelId)
   * tuple so the fetcher can search members.
   */
  creatorId: string | null | undefined;
  channelId: string | null | undefined;
}

const kPageSize = 20;
const kSearchDebounceMs = 200;

/**
 * Mention autocomplete popover — opens when the user types `@`
 * preceded by whitespace, closes when the trigger is no longer
 * matched, and inserts a `mention` element when the user picks a
 * member.
 *
 * Functionally a port of PopshopRN's `MentionPicker`, with the RxJS
 * BehaviorSubjects swapped for plain state + a debounce timer
 * (we don't need cross-component subscribe semantics here).
 */
export const ChannelComposerMentionPicker = forwardRef<MentionPickerRef, Props>(
  ({ creatorId, channelId }, ref) => {
    // `useSlate()` returns Slate's `BaseEditor` (workspace-wide
    // augmentation lives in another slice, not ours); cast to the
    // composer's editor shape so the imperative-API calls below
    // type-check.
    const editor = useSlate() as unknown as CustomEditor;
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [target, setTarget] = useState<Range | null>(null);
    const [query, setQuery] = useState('');
    const [members, setMembers] = useState<ChannelMemberItem[]>([]);
    const [index, setIndex] = useState(0);

    // Debounced member search — kicks off ~200ms after the user stops
    // typing the mention query. Empty query returns the first page
    // unfiltered so the user sees a list immediately when they hit @.
    useEffect(() => {
      if (target == null || !creatorId || !channelId) {
        setMembers([]);
        return;
      }
      let cancelled = false;
      const timer = setTimeout(() => {
        void fetchChannelMembers(creatorId, channelId, { pageSize: kPageSize })
          .then(rows => {
            if (cancelled) return;
            const filtered = query
              ? rows.filter(row => matchesQuery(row, query))
              : rows;
            setMembers(filtered);
            setIndex(0);
          })
          .catch(() => {
            if (!cancelled) setMembers([]);
          });
      }, kSearchDebounceMs);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }, [target, query, creatorId, channelId]);

    const close = useCallback(() => {
      setTarget(null);
      setQuery('');
      setMembers([]);
      setIndex(0);
    }, []);

    const handleChange = useCallback<MentionPickerRef['handleChange']>(
      nextEditor => {
        const { selection } = nextEditor;
        if (!selection || !Range.isCollapsed(selection)) {
          if (target) close();
          return false;
        }

        const [start] = Range.edges(selection);
        const lineStart = Editor.start(nextEditor, start.path);
        const beforeText = Editor.string(
          nextEditor,
          Editor.range(nextEditor, lineStart, start),
        );
        const beforeMatch = beforeText.match(/(^|\s)@(\w*)$/);

        if (!beforeMatch) {
          if (target) close();
          return false;
        }

        const after = Editor.after(nextEditor, start);
        const afterText = after
          ? Editor.string(nextEditor, Editor.range(nextEditor, start, after))
          : '';
        if (!/^(\s|$)/.test(afterText)) {
          if (target) close();
          return false;
        }

        const mentionStart = Editor.before(nextEditor, start, {
          distance: beforeMatch[2].length + 1,
        });
        if (!mentionStart) return false;
        const mentionRange = Editor.range(nextEditor, mentionStart, start);
        setTarget(mentionRange);
        setQuery(beforeMatch[2] || '');
        return false;
      },
      [target, close],
    );

    const handleKeyDown = useCallback<MentionPickerRef['handleKeyDown']>(
      (nextEditor, event) => {
        if (target == null || members.length === 0) return false;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setIndex(prev => (prev >= members.length - 1 ? 0 : prev + 1));
            return true;
          case 'ArrowUp':
            event.preventDefault();
            setIndex(prev => (prev <= 0 ? members.length - 1 : prev - 1));
            return true;
          case 'Tab':
          case 'Enter':
            event.preventDefault();
            Transforms.select(nextEditor, target);
            insertMention(nextEditor, members[index]);
            close();
            return true;
          case 'Escape':
            event.preventDefault();
            close();
            return true;
          default:
            return false;
        }
      },
      [target, members, index, close],
    );

    useImperativeHandle(ref, () => ({ handleChange, handleKeyDown }));

    // Position the popover above the @ trigger anchor — bottom-up so a
    // long list doesn't push the composer down.
    useLayoutEffect(() => {
      if (!target || members.length === 0 || !popoverRef.current) return;
      try {
        const domRange = ReactEditor.toDOMRange(editor, target);
        const rect = domRange.getBoundingClientRect();
        const el = popoverRef.current;
        el.style.bottom = `${window.innerHeight - (rect.top + window.scrollY)}px`;
        el.style.left = `${rect.left + window.scrollX}px`;
      } catch {
        // Slate occasionally raises during transient selections —
        // just skip this layout pass.
      }
    }, [editor, target, members.length, index]);

    if (!target || members.length === 0 || typeof document === 'undefined') {
      return null;
    }

    return createPortal(
      <div
        ref={popoverRef}
        className="border-line bg-surface-1 fixed z-50 max-h-64 w-64 overflow-y-auto rounded-md border shadow-xl"
      >
        <ul>
          {members.map((member, i) => {
            const active = i === index;
            const profile = member.profile;
            const name = displayName(profile);
            return (
              <li key={member.id}>
                <button
                  type="button"
                  onMouseDown={event => {
                    // Slate steals focus on click — `mousedown +
                    // preventDefault` keeps the cursor in the editor
                    // so the insert lands at the @ trigger.
                    event.preventDefault();
                    Transforms.select(editor, target);
                    insertMention(editor, member);
                    close();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                    active
                      ? 'text-content-1 bg-surface-2'
                      : 'text-content-2 hover:bg-surface-1',
                  )}
                >
                  <ChannelAvatar src={profile.avatar} name={name} size={28} />
                  <span className="min-w-0 flex-1 truncate">
                    {name}
                    {profile.username ? (
                      <span className="text-content-4 ml-1 text-xs">
                        @{profile.username}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>,
      document.body,
    );
  },
);
ChannelComposerMentionPicker.displayName = 'ChannelComposerMentionPicker';

function matchesQuery(member: ChannelMemberItem, query: string): boolean {
  const q = query.toLowerCase();
  const p = member.profile;
  return (
    (p.username ?? '').toLowerCase().includes(q) ||
    (p.firstName ?? '').toLowerCase().includes(q) ||
    (p.lastName ?? '').toLowerCase().includes(q) ||
    (p.storeFrontName ?? '').toLowerCase().includes(q)
  );
}

function displayName(profile: ChannelMemberItem['profile']): string {
  if (profile.storeFrontName) return profile.storeFrontName;
  const first = (profile.firstName ?? '').trim();
  const last = (profile.lastName ?? '').trim();
  const composed = `${first} ${last}`.trim();
  return composed || profile.username || 'Member';
}

function insertMention(editor: CustomEditor, member: ChannelMemberItem) {
  const mention: MentionElement = {
    type: 'mention',
    userId: member.id,
    profile: member.profile,
    children: [{ text: '' }],
  };

  const { selection } = editor;
  if (selection) {
    const [, end] = Range.edges(selection);
    const afterPoint = Editor.after(editor, end);
    const afterText = afterPoint
      ? Editor.string(editor, Editor.range(editor, end, afterPoint))
      : '';
    if (afterText.startsWith(' ')) {
      // Cast through `unknown`: Slate's `Node` type is augmented
      // workspace-wide to a union that doesn't include
      // `MentionElement`. Runtime shape is valid for Slate's
      // operations because the editor was wrapped with
      // `withMentions` which registers `mention` as inline + void.
      Transforms.insertNodes(editor, mention as unknown as Node);
      Transforms.move(editor);
      return;
    }
  }
  Transforms.insertNodes(editor, [
    mention as unknown as Node,
    { text: ' ' } as unknown as Node,
  ]);
  Transforms.move(editor, { distance: 1, unit: 'offset' });
}
